import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";

import "./index.css";
import "./lib/devSessionHandoff";
import { installDevStackEnterContinue } from "./lib/devStackEnterContinue";
import { installDevApiBindingGuard } from "./lib/hubDevApiBinding";
import { installHubSupportConsoleCapture } from "./lib/installHubSupportConsoleCapture";
import { installStaleBundleRecovery } from "./lib/installStaleBundleRecovery";
import { App } from "./App";
import { HubDocumentMetadataSync } from "./components/HubDocumentMetadataSync";
import { AuthProvider } from "./components/AuthProvider";
import { NavigationTrailProvider } from "./lib/navigationTrailContext";
import { HubToastProvider } from "./components/ui/HubToast";
import { IntegratorMePrefetch } from "./components/IntegratorMePrefetch";
import { HubSessionLiveGate } from "./components/HubSessionLiveGate";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { HubDevApiBindingBanner } from "./components/HubDevApiBindingBanner";
import { HubStaleVersionBanner } from "./components/HubStaleVersionBanner";
import { HubUpdatingBanner } from "./components/HubUpdatingBanner";
import { HubDbPoolAdminOverlay } from "./components/HubDbPoolAdminOverlay";
import { GoogleChatDock } from "./components/GoogleChatPanel";
import { queryClient } from "./lib/queryClient";
import { hubRouterBasename } from "./lib/devStackMount";

// Capture ?sgs_dev_enter= before React Router can strip it (overlay also does this).
installDevStackEnterContinue();
installDevApiBindingGuard();
installHubSupportConsoleCapture();
installStaleBundleRecovery();

const root = document.getElementById("root");
if (!root) {
  throw new Error("Missing #root element in index.html.");
}

/** Path-mounted stacks behind the 5173 gateway need a basename (from Vite base or URL). */
const routerBasename = hubRouterBasename();

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <HubToastProvider>
        <HubDevApiBindingBanner />
        <HubStaleVersionBanner />
        <HubUpdatingBanner />
        <BrowserRouter basename={routerBasename}>
          <NavigationTrailProvider>
            <HubDocumentMetadataSync />
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <HubDbPoolAdminOverlay />
                <GoogleChatDock />
                <HubSessionLiveGate>
                  <IntegratorMePrefetch>
                    <App />
                  </IntegratorMePrefetch>
                </HubSessionLiveGate>
              </AuthProvider>
            </QueryClientProvider>
          </NavigationTrailProvider>
        </BrowserRouter>
      </HubToastProvider>
    </ErrorBoundary>
  </StrictMode>,
);
